import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import Loading from '../../components/loading';
import SuccessMessage from '../../components/successMessage';
import ErrorMessage from '../../components/errorMessage';
import { useGeneralSettings } from './hooks/useGeneralSettings';

const MODULE_OPTIONS = [
  { value: 'ODONTO', label: 'Odonto' },
  { value: 'AESTHETIC', label: 'Estética' },
];

export default function GeneralConfig() {
  const navigate = useNavigate();
  const { fetchClinic, fetchRooms, saveClinic } = useGeneralSettings();

  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Identity fields are not edited here, but PUT /clinic is a full overwrite, so we load and re-send
  // them unchanged to avoid wiping them.
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [logoPath, setLogoPath] = useState('');

  const [usesInsurance, setUsesInsurance] = useState(false);
  const [modules, setModules] = useState([]);
  const [whatsappReminderEnabled, setWhatsappReminderEnabled] = useState(false);

  const [useAgendaRoom, setUseAgendaRoom] = useState(false);
  const [roomName, setRoomName] = useState('');
  const [rooms, setRooms] = useState([]);

  const getClinic = useCallback(() => {
    setIsLoading(true);
    fetchClinic()
      .then((returnFromApi) => {
        const data = returnFromApi.data;
        if (data) {
          setName(data.name ?? '');
          setCity(data.city ?? '');
          setAddress(data.address ?? '');
          setEmail(data.email ?? '');
          setPhone(data.phone ?? '');
          setLogoPath(data.logoPath ?? '');
          setUsesInsurance(data.usesInsurance ?? false);
          setModules(data.modules ?? []);
          setUseAgendaRoom(data.useAgendaRoom ?? false);
          setWhatsappReminderEnabled(data.useReminderMessageWhatsAppForPatient ?? false);
        }
      })
      .catch((err) => {
        console.error('Erro ao carregar dados da clínica:', err);
        if (err.status === 401) navigate('/expired-session');
      })
      .finally(() => setIsLoading(false));
  }, [navigate, fetchClinic]);

  const getRooms = useCallback(() => {
    fetchRooms()
      .then((returnFromApi) => {
        setRooms(returnFromApi.data ?? []);
      })
      .catch((err) => {
        console.error('Erro ao carregar salas:', err);
        if (err.status === 401) navigate('/expired-session');
      });
  }, [navigate, fetchRooms]);

  useEffect(() => {
    getClinic();
    getRooms();
  }, [getClinic, getRooms]);

  const handleAddRoom = () => {
    const trimmed = roomName.trim();
    if (!trimmed) return;
    setRooms((prev) => [...prev, { id: Date.now(), name: trimmed }]);
    setRoomName('');
  };

  const handleRemoveRoom = (id) => {
    setRooms((prev) => prev.filter((c) => c.id !== id));
  };

  const handleSave = () => {
    setIsLoading(true);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);

    // Send the whole clinic so untouched identity data (name, address, ...) is preserved,
    // overriding only the configuration this screen edits. `modules` and `prescriptionTemplateMode`
    // are not part of the update contract and are preserved on the backend.
    saveClinic({
      name,
      city,
      address,
      email,
      phone,
      logoPath,
      usesInsurance,
      useReminderMessageWhatsAppForPatient: whatsappReminderEnabled,
      useAgendaRoom,
    })
      .then(() => {
        setShowSuccessMessage(true);
        setSuccessMessage('Configurações salvas com sucesso!');
      })
      .catch((err) => {
        setShowErrorMessage(true);
        setErrorMessage(err.response?.data ?? 'Erro ao salvar configurações.');
        console.error('Erro ao salvar configurações:', err);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <Loading isToHidden={!isLoading} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Stack spacing={3}>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Convênios
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <FormControlLabel
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <span>Utiliza convênios</span>
                <Tooltip title="Se essa opção estiver ativada, aparecerá a tela de cadastro de convênios e os convênios cadastrados para você selecionar nos tratamentos. Basta sair e entrar novamente no sistema para que a alteração seja aplicada.">
                  <IconButton size="small" tabIndex={-1} sx={{ pointerEvents: 'auto' }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} color="action" />
                  </IconButton>
                </Tooltip>
              </Stack>
            }
            control={
              <Checkbox
                checked={usesInsurance}
                onChange={(e) => setUsesInsurance(e.target.checked)}
              />
            }
          />
        </Box>

        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Módulos
            </Typography>
            <Tooltip title="Para adicionar novos módulos a sua assinatura entre em contato com nosso suporte">
              <IconButton size="small" tabIndex={-1} sx={{ pointerEvents: 'auto' }}>
                <InfoOutlinedIcon sx={{ fontSize: 16 }} color="action" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" spacing={1}>
            {MODULE_OPTIONS.map((mod) => (
              <FormControlLabel
                key={mod.value}
                label={mod.label}
                control={
                  <Checkbox
                    checked={modules.includes(mod.value)}
                    disableRipple
                    sx={{ cursor: 'default', pointerEvents: 'none' }}
                  />
                }
                sx={{ cursor: 'default', userSelect: 'none', pointerEvents: 'none' }}
              />
            ))}
          </Stack>
        </Box>

        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Salas
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  disabled
                  checked={useAgendaRoom}
                  onChange={(e) => setUseAgendaRoom(e.target.checked)}
                />
              }
              label="Utiliza Agenda Por Salas"
            />

            {useAgendaRoom && (
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    disabled
                    label="Nome Da Sala"
                    size="small"
                    value={roomName}
                    onChange={(e) => setRoomName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddRoom()}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button variant="outlined" onClick={handleAddRoom} disabled>
                    Adicionar
                  </Button>
                </Stack>

                {rooms.length > 0 && (
                  <List dense disablePadding>
                    {rooms.map((room) => (
                      <ListItem
                        key={room.id}
                        disableGutters
                        secondaryAction={
                          <IconButton edge="end" onClick={() => handleRemoveRoom(room.id)} disabled>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText primary={room.name} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Stack>
            )}
          </Stack>
        </Box>

        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Mensagens de Lembretes por WhatsApp
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={2}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={whatsappReminderEnabled ? 'ENABLED' : 'DISABLED'}
                label="Status"
                onChange={(e) => setWhatsappReminderEnabled(e.target.value === 'ENABLED')}
              >
                <MenuItem value="ENABLED">Ativado</MenuItem>
                <MenuItem value="DISABLED">Desativado</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              Com essa opção ativada, todo dia às 9h o sistema envia automaticamente lembretes via
              WhatsApp para os pacientes com consultas agendadas para o dia seguinte.
            </Typography>
          </Stack>
        </Box>

        <Box>
          <Button variant="contained" onClick={handleSave}>
            Salvar
          </Button>
        </Box>
      </Stack>
    </>
  );
}
